import { TypedExtractor } from './interface';
// @ts-ignore
import { UnknownObj } from '@umu/umu-types';
import { requestIdleCallback } from './polyfill';

export type UnknownType = string | number | object;

export const isObject = (obj: any) => {
    return Object.prototype.toString.call(obj) === '[object Object]';
};

export function isEmptyObject(obj?: UnknownObj): boolean {
    if (typeof obj !== 'object') {
        return true;
    }
    for (const name in obj) {
        if (name) {
            return false;
        }
    }
    return true;
}

// 复制对象
export function copyObj(srcObj: UnknownObj) {
    try {
        return JSON.parse(JSON.stringify(srcObj));
    } catch (e) {
        console.error('Failed to copy object', e, srcObj);
        return srcObj;
    }
}

export const isSameObj = (obj1: UnknownObj, obj2: UnknownObj) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
};

/**
 * 剔除某些属性，获得新的 Object
 * @param object
 * @param omitProperty string[] | string
 * @returns object
 * @author Kody
 */
export const getOmitObj = (object: UnknownObj, omitProperty: string[] | string) => {
    const newObj: UnknownObj = {};
    for (const key in object) {
        if (!Object.prototype.hasOwnProperty.call(object, key)) {
            continue;
        }
        if (typeof omitProperty === 'string' && key === omitProperty) {
            continue;
        }
        if (Array.isArray(omitProperty) && omitProperty.indexOf(key) !== -1) {
            continue;
        }
        newObj[key] = object[key];
    }
    return newObj;
};

interface ISourceObject {
    [propName: string]: any;
}

/**
 * 深拷贝对象
 * @param source
 * @returns
 */
export function deepCopy<T>(source: T): T {
    if (!source || typeof source !== 'object') {
        return source;
    }
    if (source instanceof Array) {
        const copyArray: any[] = [];
        source.forEach((value) => {
            copyArray.push(deepCopy<any>(value));
        });
        return copyArray as any;
    }

    const copy: ISourceObject = {};
    if (source === Object(source)) {
        Object.keys(source).forEach((prop) => {
            copy[prop] = deepCopy<any>((source as ISourceObject)[prop]);
        });
    }
    return copy as any;
}

/**
 * @desc
 * @param
 */
export function classMapSet(classNames: { [name: string]: boolean } | string) {
    let names = '';

    if (typeof classNames === 'object') {
        for (const name in classNames) {
            if (!classNames.hasOwnProperty(name) || !classNames[name]) {
                continue;
            }
            names += name + ' ';
        }
    } else {
        for (let i = 0; i < arguments.length; i++) {
            // We should technically exclude 0 too, but for the sake of backward
            // compat we'll keep it (for now)
            if (arguments[i] == null) {
                continue;
            }
            names += arguments[i] + ' ';
        }
    }

    return names.trim();
}

/**
 * @description 根据指定key获取对象值，其中有一级别为空时即返回 undefinded , 这个source类型一定要写传主要解决可选值的类型推断。 不能被二次封装
 * 调用的地方source的类型一定要传。方法借鉴官方的object.assign, 参数最多只支持10个key.
 * @param  {object} source 数据
 * @param  {Array <string | number>} keys 对象值路径
 */
export const getData: TypedExtractor = (source: any, ...keys: Array<string | number>) => {
    let result: any = source;
    keys.forEach((key) => {
        if (result !== undefined && result !== null) {
            result = result[key];
        }
    });
    return result;
};

/**
 * 对比两个值是否相等, 弱类型比较
 * @param {string | number | object | array}
 * @return {boolean}
 */
export const isSame = (v1: any, v2: any): boolean => {
    if (v1 === v2) {
        return true;
    }

    if (v1 == null || v2 == null) {
        return v1 === v2;
    }

    if (typeof v1 === 'object' && typeof v2 === 'object') {
        if (Array.isArray(v1) && Array.isArray(v2)) {
            if (v1.length !== v2.length) {
                return false;
            }
            return v1.every((item, i) => isSame(item, v2[i]));
        }

        return isSameObj(v1 as UnknownObj, v2 as UnknownObj);
    }

    return v1 == v2;
};

/**
 * @description 对比两个对象的指定key是否相等
 * @param  {object} obj1
 * @param  {object} obj2
 * @param  {Array <string | number>} keys
 * @return {boolean}
 */
export function compareObjects<T extends object, K extends keyof T>(obj1: T, obj2: T, keysToCompare: K[]): boolean {
    return keysToCompare.every((key) => obj1[key] === obj2[key]);
}

interface ICacheOption<T> {
    initData?: Record<string | number, T>;
    scope?: string;
    useStorage?: boolean;
    useSessionStorage?: boolean;
}
export class DataCache<T> {
    private readonly data: Record<string | number, T> = Object.create(null);
    private readonly baseCacheKey = 'data_cache_';
    private readonly baseSessionCacheKey = 'data_s_cache_';

    private static readonly meta = new WeakMap<
        DataCache<any>,
        {
            created: number;
            hits: number;
        }
    >();

    private option: ICacheOption<T> = {};

    constructor(option: ICacheOption<T> = {}) {
        this.option = option || {};
        const initData = option.initData;

        if ((option.useStorage || option.useSessionStorage) && !option.scope) {
            throw new Error('Scope is required when useStorage is true');
        }

        if (option.useStorage) {
            const localData = localStorage.getItem(this.getCacheKey('local'));
            this.feedStorageData(localData);
        }

        if (option.useSessionStorage) {
            const sessionData = sessionStorage.getItem(this.getCacheKey('session'));
            this.feedStorageData(sessionData);
        }

        for (const key in initData) {
            if (Object.prototype.hasOwnProperty.call(initData, key)) {
                this.data[this.getKey(key)] = initData[key];
            }
        }

        DataCache.meta.set(this, {
            created: Date.now(),
            hits: 0,
        });

        if (option.initData) {
            this.updateStorage();
        }
    }

    getCacheKey(from: 'local' | 'session') {
        if (!from) {
            return this.option.scope || '';
        }
        return `${this[from === 'local' ? 'baseCacheKey' : 'baseSessionCacheKey']}${this.option.scope}`;
    }

    feedStorageData(storageData: string | null) {
        if (storageData) {
            try {
                const data = JSON.parse(storageData);
                for (const key in data) {
                    if (Object.prototype.hasOwnProperty.call(data, key)) {
                        this.data[key] = data[key];
                    }
                }
            } catch (e) {
                console.error('Failed to parse data from storage', e, storageData);
            }
        }
    }

    getKey(k: string | number) {
        return this.option.scope ? `${this.option.scope}_${k}` : k;
    }

    setValue(key: string | number, value: T): void {
        this.data[this.getKey(key)] = value;
        this.updateStorage();
    }

    getValue(key?: string | number, defaultValue?: T) {
        if (key === void 0) {
            return defaultValue;
        }
        const meta = DataCache.meta.get(this);
        if (meta) {
            meta.hits++;
        }

        return this.data[this.getKey(key)];
    }

    hasKey(key: string | number): boolean {
        return Object.prototype.hasOwnProperty.call(this.data, this.getKey(key));
    }

    clear(): void {
        for (const key in this.data) {
            if (Object.prototype.hasOwnProperty.call(this.data, key)) {
                delete this.data[key];
            }
        }
        this.updateStorage();
    }

    updateStorage() {
        const { useStorage, useSessionStorage } = this.option;
        if (!useStorage && !useSessionStorage) {
            return;
        }
        requestIdleCallback(() => {
            const localKey = this.getCacheKey('local');
            const sessionKey = this.getCacheKey('session');
            try {
                useStorage && localStorage.setItem(localKey, JSON.stringify(this.data));
                useSessionStorage && sessionStorage.setItem(sessionKey, JSON.stringify(this.data));
            } catch (e) {
                console.error(`Failed to write to storage - ${this.option.scope}`, e);
            }
        });
    }

    // 获取缓存统计信息
    getStats() {
        const meta = DataCache.meta.get(this);
        return meta
            ? {
                  age: Date.now() - meta.created,
                  hits: meta.hits,
                  size: this.size(),
              }
            : null;
    }

    size(): number {
        return Object.keys(this.data).length;
    }
}
